import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { CardDetailsToggle } from 'components/card-details-toggle';
import style from './index.module.scss';

interface Props {
  className?: string;
  /** The tiles' container: one element whose children are the tiles. */
  children: React.ReactElement;
}

/**
 * N2 (T3, DC-21): below md a tab's summary tiles collapse into one summary
 * line -- the first two tiles' values, their labels hidden -- and a tap on the
 * line opens the tiles in full ("פירוט", the cards' details toggle, closes
 * them again). Used on the patient card's appointments and weights tabs; the
 * general tab keeps its tiles open.
 *
 * Presentation only: the tiles render exactly as before (the same components,
 * the same requests), the collapsed line is their own first two values laid
 * out in one row by this module's CSS. While collapsed, a tap anywhere on the
 * line opens it -- captured before the tiles see it (a click, or Enter / Space),
 * so a tile container that is itself a button (the weights box opens its
 * history) does not fire.
 * Above md the children are returned untouched: the desktop DOM is unchanged.
 */
export const TilesSummary: React.FC<Props> = ({ className, children }) => {
  const isBelowMd = useIsBelowMd();
  const [isOpen, setIsOpen] = useState(false);

  const onToggle = useCallback(() => setIsOpen((value) => !value), []);
  const onCaptureOpen = useCallback(
    (e: React.MouseEvent) => {
      if (isOpen) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(true);
    },
    [isOpen],
  );
  // the keyboard's way to the same tap: a tile container that is a ButtonBase
  // fires its onClick from Enter / Space without a click event
  const onCaptureKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (isOpen || (e.key !== 'Enter' && e.key !== ' ')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(true);
    },
    [isOpen],
  );

  if (!isBelowMd) {
    return children;
  }

  return (
    <div
      className={clsx(style.root, !isOpen && style.collapsed, className)}
      onClickCapture={onCaptureOpen}
      onKeyDownCapture={onCaptureKey}
    >
      {children}
      <CardDetailsToggle className={style.toggle} expanded={isOpen} onToggle={onToggle} />
    </div>
  );
};
